/* GlowSpineControls.jsx — the 5 glow-spine tuning sliders, styled for the
 * SettingsModal debug block (not the standalone floating panel from the
 * handoff). Rendered only when debugUI && glowSpine are both on. Values are held
 * in App.jsx (persisted via usePersistedState) and passed through GlowSpine's
 * `controls` prop; the "Copy values as JSON" button dumps the current set so a
 * final tuning can be pasted into GLOW_DEFAULTS.
 *
 * Props: { value, onChange, C }  — value/onChange is the controls object +
 * setter; C is the theme color object so the sliders match the active theme.
 */
import React from "react";
import { copyToClipboard } from "../../lib/util/clipboard";
import { GLOW_DEFAULTS } from "../../lib/timeline/glowSpineModel";
import { ui } from "../../lib/telemetry/instrument.js";

const ROWS = [
  { key: "amp", label: "Undulation amplitude", min: 0, max: 6, step: 0.1, suffix: "px" },
  { key: "speed", label: "Flow speed", min: 0, max: 2, step: 0.05, prefix: "×" },
  { key: "fadeLeft", label: "Past fade (left)", min: 0, max: 0.5, step: 0.01, pct: true },
  { key: "fadeRight", label: "Forecast trail (right)", min: 0, max: 0.4, step: 0.01, pct: true },
  { key: "glow", label: "Glow strength", min: 0, max: 2, step: 0.05, prefix: "×" },
];

export default function GlowSpineControls({ value, onChange, C }) {
  const accent = C?.action || C?.purpleL || "#cc99ff";
  const label = C?.gray || "#667788";
  const readout = C?.grayL || "#aabbcc";
  // Merge over GLOW_DEFAULTS so a null/undefined or key-sparse persisted value
  // (e.g. from older localStorage) can't produce NaN readouts, an uncontrolled
  // range input, or an `undefined` clipboard dump — every key resolves to a number.
  const controls = { ...GLOW_DEFAULTS, ...(value && typeof value === "object" ? value : {}) };
  const set = (k, v) => onChange({ ...controls, [k]: v });
  const fmt = (r) => {
    const v = controls[r.key];
    if (r.pct) return `${Math.round(v * 100)}%`;
    if (r.prefix) return `${r.prefix}${Number(v).toFixed(2)}`;
    return `${v}${r.suffix || ""}`;
  };
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
      {ROWS.map((r) => (
        <div key={r.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: label,
            }}
          >
            <span>{r.label}</span>
            <span style={{ color: readout }}>{fmt(r)}</span>
          </div>
          <input
            {...ui("glow-spine.tuning", r.key)}
            type="range"
            min={r.min}
            max={r.max}
            step={r.step}
            value={controls[r.key]}
            onChange={(e) => set(r.key, parseFloat(e.target.value))}
            style={{ width: "100%", accentColor: accent }}
          />
        </div>
      ))}
      <button
        {...ui("glow-spine.copy-values")}
        type="button"
        onClick={() => copyToClipboard(JSON.stringify(controls, null, 2))}
        style={{
          width: "100%",
          marginTop: 2,
          padding: "6px 0",
          fontSize: 11,
          fontFamily: "inherit",
          background: "rgba(153,102,221,0.14)",
          color: accent,
          border: `1px solid ${C?.border || "#1e2a44"}`,
          borderRadius: 5,
          cursor: "pointer",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Copy values as JSON
      </button>
    </div>
  );
}
